import { formatDate } from "@angular/common";
import { Component, DoCheck, Input, OnDestroy, OnInit } from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { cloneDeep, debounce, isEqual } from "lodash";
import { forkJoin, of } from "rxjs";
import { finalize } from "rxjs/operators";
import { EventService } from "../event-service/event.service";
import { FormatDeployLabelService } from "../format-deploy-label/format-deploy-label.service";
import { AttributeService } from "../models/attribute.service";
import { DeployService } from "../models/deploy.service";
import { PlanTierService } from "../plan-tier/plan-tier.service";
import { SearchDataService } from "./search-data/search-data.service";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const CODE_EXECUTION_DELAY = 10000;

@Component({
  templateUrl: "./group-filter.component.html",
})
export class GroupFilterComponent implements OnInit, DoCheck, OnDestroy {
  @Input() public filters: any;
  @Input() public project: any;

  public selectedParams: any[];
  public availableParams: any[];
  public searchQuery: string;
  public prevFilters: any;
  public showUpgradeCta: any;
  public showCustomFiltersLink: boolean;
  public addedSearchParamSub: any;
  public removedSearchParamsSub: any;
  public updatedParamsSub: any;
  public updatedQuerySub: any;

  private nonAttrKeys = [
    "end_time",
    "has_comments",
    "muted",
    "on",
    "order",
    "resolved",
    "search",
    "severity",
    "start_time",
  ];

  private deployKeys = [
    "deploy_id",
    "resolved_by_deploy_id",
    "unresolved_by_deploy_id",
  ];

  private onFiltersUpdated = debounce(() => {
    this.updateUrlFromSearch();
    this.eventService.publish("groups:filter");
  }, 250);

  constructor(
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private attributeService: AttributeService,
    private deployService: DeployService,
    private eventService: EventService,
    private formatDeployLabelService: FormatDeployLabelService,
    private planTierService: PlanTierService,
    private searchDataService: SearchDataService,
  ) {}

  public ngDoCheck() {
    if (isEqual(this.filters, this.prevFilters)) {
      return;
    }

    this.prevFilters = cloneDeep(this.filters);
    this.onFiltersUpdated();
  }

  public ngOnInit() {
    this.selectedParams = [];
    this.availableParams = [];
    this.searchQuery = "";
    this.prevFilters = cloneDeep(this.filters);

    // Update URL with modified param filters from the state.
    const paramFilters = cloneDeep(this.filters);
    delete paramFilters.projectId;
    this.$state.go(this.$uiRouterGlobals.current.name, {
      filters: paramFilters,
    });

    this.searchDataService
      .loadData(this.project.id, this.project.attributes)
      .pipe(
        finalize(() => this.eventService.publish("groups:searchDataLoaded")),
      )
      .subscribe(this.updateSearchFromUrl.bind(this));

    this.showUpgradeCta = {};
    this.planTierService.features().subscribe((features: any) => {
      this.showUpgradeCta = {
        advancedSearch: !features.advanced_search,
        customFilters: features.advanced_search && !features.custom_search,
      };

      const upgradeCtaPresent =
        this.showUpgradeCta.advancedSearch || this.showUpgradeCta.customFilters;
      const projectHasCustomAttrs =
        this.project.attributes != null && this.project.attributes.length > 0;
      this.showCustomFiltersLink = !upgradeCtaPresent && !projectHasCustomAttrs;
    });

    this.addedSearchParamSub = this.eventService.subscribe(
      "advanced-searchbox:addedSearchParam",
      (searchParam: any) => {
        switch (searchParam.key) {
          case "on":
            this.removeParam("start_time");
            this.removeParam("end_time");
            this.removeFilter("start_time");
            this.removeFilter("end_time");
            break;
          case "start_time":
            if (
              this.filters.start_time == null ||
              this.filters.end_time == null
            ) {
              return;
            }
            this.removeParam("on");
            this.removeParam("end_time");
            this.removeFilter("end_time");
            break;
          case "end_time":
            if (
              this.filters.start_time == null ||
              this.filters.end_time == null
            ) {
              return;
            }
            this.removeParam("on");
            this.removeParam("start_time");
            this.removeFilter("start_time");
            break;
        }
      },
    );

    this.removedSearchParamsSub = this.eventService.subscribe(
      "advanced-searchbox:removedSearchParams",
      (searchParams: any[]) => {
        this.removeFilters(searchParams);
      },
    );

    this.updatedParamsSub = this.eventService.subscribe(
      "advanced-searchbox:updatedParams",
      (params: any[]) => {
        this.updateFilters(params);
      },
    );

    this.updatedQuerySub = this.eventService.subscribe(
      "advanced-searchbox:updatedQuery",
      (query: string) => {
        this.searchQuery = query;
        if (query === "") {
          return this.removeFilter("search");
        } else {
          return this.updateFilter("search", query);
        }
      },
    );
  }

  public ngOnDestroy() {
    this.addedSearchParamSub.unsubscribe();
    this.removedSearchParamsSub.unsubscribe();
    this.updatedParamsSub.unsubscribe();
    this.updatedQuerySub.unsubscribe();
  }

  // sets up a search param from an individual URL param
  private updateSearchParamFromUrlParam(searchParam: any, urlParam: any) {
    if (searchParam.type === "date") {
      // convert unix timestamp to JS date object
      const date = new Date(this.utcMidnight(urlParam * 1000));
      searchParam.value = date;
      searchParam.label = formatDate(date, "yyyy-MM-dd", "en-US", "UTC");
    } else {
      searchParam.suggestions().subscribe((suggestions: any) => {
        for (const suggestion of suggestions) {
          if (suggestion.value === urlParam) {
            searchParam.value = suggestion.value;
            searchParam.label = suggestion.label;
            return;
          }
        }

        // no matching suggestion found
        this.removeFilter(searchParam.key);
        searchParam.value = searchParam.label = null;
      });
    }
  }

  private updateSearchParamFromDeployParam(
    searchParam: any,
    deployId: any,
    deployData: any,
  ) {
    for (const deploy of deployData) {
      if (deployId === deploy.id) {
        searchParam.label = this.formatDeployLabelService.labelFor(deploy);
        searchParam.value = deploy.id;
        searchParam.groupCount = deploy.groupCount;
        break;
      }
    }
  }

  private updateSearchParamFromUrlAttrParam(searchParam: any, attrData: any) {
    for (const attr of attrData) {
      if (attr.attribute != null) {
        if (searchParam.key === attr.attribute.key) {
          searchParam.label = searchParam.suggestionFormat(
            attr.attribute.value,
          );
          searchParam.value = attr.attribute.valueId;
          break;
        }
      }
    }
  }

  private updateSearchFromUrl(searchData: any) {
    const paramFilters = this.$uiRouterGlobals.params.filters || {};
    const attrObservables = [];
    const deployObservables = [];

    for (const key of Object.keys(paramFilters)) {
      if (this.nonAttrKeys.indexOf(key) !== -1) {
        continue;
      }

      const value = paramFilters[key];
      if (value == null || value === 0 || value.length === 0) {
        continue;
      }

      if (this.deployKeys.indexOf(key) !== -1) {
        deployObservables.push(
          this.deployService.get({
            projectId: this.project.id,
            deployId: value,
          }),
        );
      } else {
        const params = { projectId: this.project.id, attrId: value };
        attrObservables.push(this.attributeService.get(params));
      }
    }
    const attrObservableCount = attrObservables.length;
    const deployObservableCount = deployObservables.length;
    const totalObservableCount = attrObservableCount + deployObservableCount;
    let observables = attrObservables.concat(deployObservables);
    if (observables.length === 0) {
      observables = [of([])];
    }

    forkJoin(observables).subscribe((data: any) => {
      const attrData = data.slice(0, attrObservableCount);
      const deployData = data.slice(attrObservableCount, totalObservableCount);
      // set label and value based on query params present in the URL
      for (const searchParam of searchData) {
        if (paramFilters[searchParam.key] != null) {
          if (this.deployKeys.indexOf(searchParam.key) !== -1) {
            this.updateSearchParamFromDeployParam(
              searchParam,
              paramFilters[searchParam.key],
              deployData,
            );
          } else if (this.nonAttrKeys.indexOf(searchParam.key) !== -1) {
            this.updateSearchParamFromUrlParam(
              searchParam,
              paramFilters[searchParam.key],
            );
          } else {
            this.updateSearchParamFromUrlAttrParam(searchParam, attrData);
          }
        }

        if (searchParam.value != null && !searchParam.featureDisabled) {
          this.selectedParams.push(searchParam);
        } else {
          this.availableParams.push(searchParam);
        }
      }

      this.updateFilters(this.selectedParams);

      if (paramFilters.search) {
        this.searchQuery = paramFilters.search;
        this.updateFilter("search", this.searchQuery);
      }
    });
  }

  private updateUrlFromSearch() {
    const newParams: any = { order: this.filters.order, resolved: "any" };

    for (const param of this.selectedParams) {
      if (param.value == null) {
        continue;
      }

      if (param.type === "date") {
        newParams[param.key] = Date.parse(param.value) / 1000;
      } else {
        newParams[param.key] = param.value;
      }
    }

    if (this.searchQuery != null && this.searchQuery !== "") {
      newParams.search = this.searchQuery;
    }

    return this.$state.go(this.$uiRouterGlobals.current.name, {
      filters: newParams,
    });
  }

  private utcMidnight(date: any) {
    if (this.withinOneDay(date)) {
      return date;
    }

    return new Date(date).setUTCHours(0, 0, 0, 0);
  }

  private withinOneDay(date: any): boolean {
    const currentTime = new Date().getTime();

    return (
      currentTime - new Date(date).getTime() < ONE_DAY_MS + CODE_EXECUTION_DELAY
    );
  }

  private updateTimeFilter(filter: string, date: any) {
    return this.updateFilter(filter, Math.round(this.utcMidnight(date) / 1000));
  }

  private updateFilter(key: string, value: any) {
    return (this.filters[key] = value);
  }

  private removeStateParam(key: string) {
    if (key === "resolved") {
      return this.$state.go(this.$uiRouterGlobals.current, {
        filters: { resolved: "any" },
      });
    }

    const newFilters = cloneDeep(this.$uiRouterGlobals.params.filters);
    delete newFilters[key];

    return this.$state.go(this.$uiRouterGlobals.current, {
      filters: newFilters,
    });
  }

  private removeFilter(key: string) {
    this.removeStateParam(key);

    if (key === "on") {
      delete this.filters.start_time;
      delete this.filters.end_time;
    } else {
      delete this.filters[key];
    }
  }

  private removeParam(key: string) {
    this.eventService.publish("group-filter:removeParam", key);
  }

  private updateFilters(params: any[]) {
    for (const param of params) {
      if (param.value == null) {
        continue;
      }
      switch (param.key) {
        case "on":
          const start = new Date(param.value);
          this.updateTimeFilter("start_time", start);
          const end = new Date(start.setDate(start.getDate() + 1));
          this.updateTimeFilter("end_time", end);
          break;
        case "start_time":
          this.updateTimeFilter("start_time", param.value);
          break;
        case "end_time":
          this.updateTimeFilter("end_time", param.value);
          break;
        default:
          this.updateFilter(param.key, param.value);
      }
    }
  }

  private removeFilters(params: any[]) {
    for (const param of params) {
      this.removeFilter(param.key);
    }
  }
}
